import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import {
  format,
  startOfYear,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  eachMonthOfInterval,
  isSameMonth,
  isToday,
  isValid,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getScheduleColour } from '../../../constants';

const WEEK_STARTS_ON = 1;
const MAX_DOTS = 3;

const useStyles = makeStyles(() => ({
  yearView: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    border: '1px solid #DADCE0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    padding: '10px 12px 12px',
    boxSizing: 'border-box',
    scrollbarWidth: 'thin',
    scrollbarColor: '#BFE3F3 transparent',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#BFE3F3',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#0B9FD9',
    },
  },
  monthsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '16px 20px',
    alignContent: 'start',
    width: '100%',
  },
  monthCard: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  monthTitle: {
    flexShrink: 0,
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#0B9FD9',
    lineHeight: 1.3,
    marginBottom: 4,
    padding: '0 2px',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    width: 'fit-content',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'underline',
      color: '#117CA8',
    },
  },
  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    flexShrink: 0,
    marginBottom: 2,
  },
  weekdayCell: {
    textAlign: 'center',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#70757a',
    lineHeight: 1.2,
    textTransform: 'uppercase',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  },
  dayCell: {
    position: 'relative',
    minWidth: 0,
    height: 30,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 1px 2px',
    border: 'none',
    background: 'transparent',
    borderRadius: 4,
  },
  dayCellClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F3FAFD',
    },
  },
  dayNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    borderRadius: '50%',
    fontSize: '0.85rem',
    fontWeight: 400,
    color: '#3c4043',
    lineHeight: 1,
  },
  dayNumberOutside: {
    color: '#b0b3b5',
  },
  dayNumberToday: {
    backgroundColor: '#0B9FD9',
    color: '#FFFFFF',
    fontWeight: 700,
  },
  dayNumberHasTasks: {
    fontWeight: 600,
  },
  dotsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    minHeight: '6px',
    marginTop: '2px',
    lineHeight: '0px',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },
  dotDone: {
    opacity: 0.72,
  },
}));

const toDayKey = (date) => format(date, 'yyyy-MM-dd');

const YearView = (props) => {
  const { data, focusYear, onItemClick, onOpenDayDialog, onMonthClick } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const currentMonthRef = useRef(null);

  const yearAnchor = useMemo(() => {
    const parsed = focusYear ? new Date(focusYear) : new Date();
    return isValid(parsed) ? parsed : new Date();
  }, [focusYear]);

  const tasksByDay = useMemo(() => {
    const map = {};
    (data || []).forEach((item) => {
      if (!item?.scheduleDate) return;
      const itemDate = new Date(item.scheduleDate);
      if (!isValid(itemDate)) return;
      const key = toDayKey(itemDate);
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [data]);

  const months = useMemo(() => {
    const yearStart = startOfYear(yearAnchor);
    return eachMonthOfInterval({
      start: yearStart,
      end: new Date(yearStart.getFullYear(), 11, 1),
    });
  }, [yearAnchor]);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const target = currentMonthRef.current;
    if (!container || !target || !container.contains(target)) return;

    const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTop = Math.max(top - 8, 0);
  }, [yearAnchor]);

  const weekdayLabels = useMemo(() => {
    const start = startOfWeek(yearAnchor, { weekStartsOn: WEEK_STARTS_ON });
    const end = endOfWeek(yearAnchor, { weekStartsOn: WEEK_STARTS_ON });
    return eachDayOfInterval({ start, end });
  }, [yearAnchor]);

  const buildMonthDays = (monthDate) => {
    const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: WEEK_STARTS_ON });
    const gridEnd = endOfWeek(endOfMonth(monthDate), { weekStartsOn: WEEK_STARTS_ON });
    return eachDayOfInterval({ start: gridStart, end: gridEnd }).map((day) => {
      const tasks = tasksByDay[toDayKey(day)] || [];
      return {
        date: day,
        tasks,
        inMonth: isSameMonth(day, monthDate),
      };
    });
  };

  const handleOpenDay = (event, date, tasks) => {
    event.stopPropagation();
    if (!tasks.length) return;
    if (tasks.length === 1 && onItemClick) {
      onItemClick(tasks[0]);
      return;
    }
    if (onOpenDayDialog) onOpenDayDialog(date);
  };

  const handleMonthTitleClick = (event, monthDate) => {
    event.stopPropagation();
    if (onMonthClick) onMonthClick(monthDate);
  };

  const renderDots = (tasks) => {
    if (!tasks.length) return <div className={clsx(classes.dotsRow)} />;
    const visible = tasks.slice(0, MAX_DOTS);
    return (
      <div className={clsx(classes.dotsRow)}>
        {visible.map((task) => (
          <span
            key={task?.id || `${task?.scheduleDate}-${task?.title}`}
            className={clsx(classes.dot, task?.isDone && classes.dotDone)}
            style={{ backgroundColor: getScheduleColour(task) }}
            title={task?.title || ''}
          />
        ))}
      </div>
    );
  };

  return (
    <div ref={scrollRef} className={clsx(classes.yearView)}>
      <div className={clsx(classes.monthsGrid)}>
        {months.map((monthDate) => {
          const monthDays = buildMonthDays(monthDate);
          const isCurrentMonth = isSameMonth(monthDate, new Date());

          return (
            <div
              key={format(monthDate, 'yyyy-MM')}
              ref={isCurrentMonth ? currentMonthRef : undefined}
              className={clsx(classes.monthCard)}
            >
              <button
                type="button"
                className={clsx(classes.monthTitle)}
                onClick={(event) => handleMonthTitleClick(event, monthDate)}
                aria-label={t('dashboard:SCHEDULE_OPEN_MONTH', { month: format(monthDate, 'MMMM yyyy') })}
                title={t('dashboard:SCHEDULE_OPEN_MONTH', { month: format(monthDate, 'MMMM yyyy') })}
              >
                {format(monthDate, 'MMMM')}
              </button>
              <div className={clsx(classes.weekdayRow)}>
                {weekdayLabels.map((day) => (
                  <Typography key={day.toISOString()} className={clsx(classes.weekdayCell, 'cursor-default')}>
                    {format(day, 'EEEEE')}
                  </Typography>
                ))}
              </div>
              <div className={clsx(classes.daysGrid)}>
                {monthDays.map(({ date, tasks, inMonth }) => {
                  const today = isToday(date);
                  const hasTasks = inMonth && tasks.length > 0;
                  const taskCount = tasks.length;

                  const dayContent = (
                    <>
                      <span
                        className={clsx(
                          classes.dayNumber,
                          !inMonth && classes.dayNumberOutside,
                          today && classes.dayNumberToday,
                          hasTasks && !today && classes.dayNumberHasTasks,
                        )}
                      >
                        {format(date, 'd')}
                      </span>
                      {hasTasks ? renderDots(tasks) : <div className={clsx(classes.dotsRow)} />}
                    </>
                  );

                  if (hasTasks) {
                    return (
                      <button
                        key={date.toISOString()}
                        type="button"
                        className={clsx(classes.dayCell, classes.dayCellClickable)}
                        onClick={(event) => handleOpenDay(event, date, tasks)}
                        aria-label={t('dashboard:SCHEDULE_DAY_COUNT', { count: taskCount })}
                        title={`${format(date, 'EEE d MMM')} · ${t('dashboard:SCHEDULE_DAY_COUNT', {
                          count: taskCount,
                        })}`}
                      >
                        {dayContent}
                      </button>
                    );
                  }

                  return (
                    <div key={date.toISOString()} className={clsx(classes.dayCell)}>
                      {dayContent}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default YearView;
